import { useState } from 'react';
import { useNotify } from 'ra-core';
import { lireLesChiffresDuParc } from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { deballer, lireLErreur } from '../../fournisseurs/client';

type ChiffresDuParc = {
  periode: { debut: string; fin: string };
  perteDeContenu: { nombre: number; partDesCours: number };
  chuteDuCanal: { nombre: number; partDesCours: number };
  fermeturesGlobalesDeConnexions: number;
  coursTenus: number;
  elevesDistincts: number;
  dureeDobservationSecondes: null;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ÉCRAN RÉSERVÉ AU COMPTE QUI VOIT TOUT — F-67, SECOND DES DEUX.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-67 : « **l'écran réservé au compte qui voit tout** porte les **deux indicateurs de perte de
 * contenu**, les **deux de chute du canal**, le **nombre de fermetures globales de connexions**,
 * et le **douzième indicateur**. **Parc entier, jamais par structure**, et **la personne qui
 * tient un espace ne les voit nulle part : refus explicite.** »
 *
 * **Le motif du refus, déjà écrit et non rejoué** : « une structure ne peut rien sur les
 * liaisons de ses élèves ni sur les sessions de ses personnes, **et la seule décision qui
 * s'agit sur ces chiffres — le rejeu du mode de diffusion — appartient au parc**. »
 *
 * §7 en fait une exigence de forme : « **un `orgadmin` reçoit `403 CHIFFRES_DE_PARC_INTERDITS`,
 * jamais un objet vide** — une absence et un refus se lisent pareil. » Et D-074 a posé
 * `@CodeDeRefus` sur cette route **pour que le refus porte ce nom** : `AuthorizationGuard` le
 * refusait correctement, « mais sous le nom générique `FUNCTION_NOT_ALLOWED` », et « un front
 * qui attend le code nommé ne l'aurait jamais vu ».
 *
 * **La période est OBLIGATOIRE ici**, là où elle est facultative sur l'écran ouvert : §7 la
 * déclare « paramètre obligatoire », et F-67 en donne la raison — « sans période, "nombre de
 * chutes" ou "nombre de fermetures" ne veut rien dire — **et le douzième indicateur le suit** ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE DOUZIÈME INDICATEUR EST LE SEUL QUI DISE SI LE PRODUIT SERT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-67 (Q552) : « le **nombre de cours tenus** et le **nombre d'élèves distincts ayant eu un
 * cours**, sur une période. **C'est le premier indicateur du produit qui ne soit pas
 * d'exploitation** : les onze autres disent si un réglage est bien calibré ou si une décision
 * technique doit être rejouée, **celui-ci dit si le produit sert**. C'est **le seul qui réponde
 * à la cible de succès du cadrage** — cent élèves faisant un cours hebdomadaire à douze mois —,
 * qu'aucun chiffre du produit ne mesurait. »
 *
 * Deux bornes de comptage, écrites : « **un élève distinct se compte UNE FOIS**, quel que soit
 * son nombre de cours et **quel que soit son nombre de rattachements** » ; et « **un cours
 * masqué compte quand même** dans ce chiffre : **le masquage vise la liste d'une personne,
 * jamais la mémoire du parc.** » L'écran les dit — sans quoi un chiffre qui inclut les cours
 * masqués passerait pour une erreur.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CES QUATRE PREMIERS CHIFFRES ONT COÛTÉ, ET CE QU'ILS NE SONT PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-67 (Q568) : « **ce sur quoi ils se calculent existe désormais au modèle.** Les **quatre
 * relevés** — perte de contenu, chute de canal, coupure imposée d'un flux, observation d'un
 * cours — **étaient nommés par `CDC-2` et absents du modèle**, où une seule table de trace
 * portait tout. **Sans eux, aucun des quatre premiers chiffres réservés n'était calculable.** »
 *
 * Et la borne qui tient malgré eux : « **un agrégat n'est pas une consultation de trace** :
 * "aucun écran de consultation de trace n'existe en V1" vaut sans changement — **les lignes ne
 * sont montrées par aucun écran, seuls leurs comptes le sont.** » C'est pourquoi cet écran ne
 * porte **aucune liste**, sur aucun des six chiffres : une liste ferait l'écran que la borne
 * refuse.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA DURÉE D'OBSERVATION EST AU CONTRAT, ET ELLE EST VIDE — D-081.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-64 promet qu'« un chiffre compte la durée d'une observation », et F-67 ne l'énumérait
 * pas : c'était l'écart de D-073. **Le porteur a tranché le 20260824 — attendre, ne pas
 * combler.**
 *
 * **Et l'écart avec les deux indicateurs retirés de l'écran ouvert est exactement le fait
 * qui a décidé.** Ceux-là n'avaient **aucune source au modèle** : combler demandait une
 * migration, donc ils sont retirés du périmètre (D-080). Celui-ci **a sa source** —
 * `LiveWatchEntry` porte `startedAt` et `endedAt`, la durée est calculable aujourd'hui, en
 * une requête.
 *
 * **Ce qui manque n'est pas la donnée, c'est le sens.** D-073 : le canal **n'admet pas
 * encore d'observateur**, `admettre()` exigeant d'être participant du cours, donc **toutes
 * les durées valent zéro**. Un zéro exact serait **indistinguable de « personne
 * n'observe »** — la confusion même que D-075 refuse en rendant ses indicateurs vides
 * plutôt qu'à zéro.
 *
 * **Le champ est donc présent et vide**, et les deux moitiés comptent : présent, parce que
 * la promesse de F-64 tient et qu'un champ absent se lirait comme une promesse abandonnée ;
 * vide, parce que le produit ne peut pas encore affirmer une durée. **Il se remplira sans
 * aucune décision nouvelle** — le jour où le canal ouvre ce chemin, seul le service change.
 */
export function ChiffresDuParc() {
  const notifier = useNotify();
  const [chiffres, setChiffres] = useState<ChiffresDuParc | null>(null);
  const [periode, setPeriode] = useState<{ du: string; au: string }>({ du: '', au: '' });
  const [enCours, setEnCours] = useState(false);

  const lire = async (evenement: React.FormEvent<HTMLFormElement>) => {
    evenement.preventDefault();
    if (periode.du === '' || periode.au === '') {
      // **La période est obligatoire, et l'écran le dit avant l'appel.** Sans elle le serveur
      // refuse en validation : un `400` de validation là où une phrase suffit ferait passer une
      // règle du cahier des charges pour une panne.
      notifier(
        'La période est obligatoire sur cet écran : sans elle, « nombre de chutes » ne veut ' +
          'rien dire.',
        { type: 'warning' },
      );
      return;
    }
    setEnCours(true);
    try {
      const reponse = await deballer(
        lireLesChiffresDuParc({
          query: {
            du: new Date(periode.du).toISOString(),
            au: new Date(periode.au).toISOString(),
          },
        }),
      );
      setChiffres(reponse);
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      notifier(
        lue.code === 'CHIFFRES_DE_PARC_INTERDITS'
          ? 'Ces chiffres portent sur le parc entier et ne sont pas ouverts à une structure.'
          : lue.message,
        { type: 'error' },
      );
    } finally {
      setEnCours(false);
    }
  };

  return (
    <main className="flex max-w-4xl flex-col gap-6 p-6">
      <h2 className="text-lg font-medium">Chiffres réservés — parc entier</h2>
      <p className="text-muted-foreground text-sm">
        Parc entier, jamais par structure. Aucune ligne de trace n’est montrée : seuls leurs
        comptes le sont. Aucune alerte, aucun seuil, aucun email ne s’attache à ces chiffres.
      </p>

      <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void lire(e)}>
        <div className="flex flex-col gap-1">
          <Label htmlFor="du-parc">Du</Label>
          <Input
            id="du-parc"
            type="date"
            required
            value={periode.du}
            onChange={(e) => setPeriode((p) => ({ ...p, du: e.target.value }))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="au-parc">Au</Label>
          <Input
            id="au-parc"
            type="date"
            required
            value={periode.au}
            onChange={(e) => setPeriode((p) => ({ ...p, au: e.target.value }))}
          />
        </div>
        <Button type="submit" disabled={enCours}>
          Calculer
        </Button>
        <span className="text-muted-foreground text-xs">
          La période est obligatoire ici : un nombre d’événements sans période ne veut rien dire.
        </span>
      </form>

      {chiffres !== null && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Chiffre titre="Pertes de contenu" valeur={String(chiffres.perteDeContenu.nombre)} />
            <Chiffre
              titre="Part des cours avec perte"
              valeur={`${Math.round(chiffres.perteDeContenu.partDesCours * 100)} %`}
            />
            <Chiffre titre="Chutes du canal" valeur={String(chiffres.chuteDuCanal.nombre)} />
            <Chiffre
              titre="Part des cours avec chute"
              valeur={`${Math.round(chiffres.chuteDuCanal.partDesCours * 100)} %`}
            />
            <Chiffre
              titre="Fermetures globales de connexions"
              valeur={String(chiffres.fermeturesGlobalesDeConnexions)}
            />
          </div>

          {/* **Le douzième, à part — parce qu'il n'est pas d'exploitation.** */}
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">Est-ce que le produit sert ?</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Chiffre titre="Cours tenus" valeur={String(chiffres.coursTenus)} />
              <Chiffre
                titre="Élèves distincts ayant eu un cours"
                valeur={String(chiffres.elevesDistincts)}
              />
            </div>
            <p className="text-muted-foreground text-xs">
              Un élève se compte une fois, quel que soit son nombre de cours et de
              rattachements. Un cours masqué compte quand même : le masquage vise la liste d’une
              personne, jamais la mémoire du parc. C’est le seul chiffre qui réponde à la cible
              du cadrage — cent élèves faisant un cours hebdomadaire à douze mois.
            </p>
          </section>

          {/* **Présent et vide, et les deux moitiés comptent** — D-081. */}
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">Durée des observations</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Chiffre
                titre="Durée cumulée des observations"
                valeur={
                  chiffres.dureeDobservationSecondes === null
                    ? 'indisponible'
                    : `${Math.round(chiffres.dureeDobservationSecondes / 60)} min`
                }
                attenue={chiffres.dureeDobservationSecondes === null}
              />
            </div>
            <p className="text-muted-foreground text-xs">
              Indisponible, et ce n’est pas zéro. La source existe — chaque passage laisse sa
              ligne avec ses deux instants —, mais le canal temps réel n’admet pas encore
              d’observateur, donc toutes les durées vaudraient zéro. Afficher zéro affirmerait
              « personne n’observe », ce qui n’est pas la même chose. Ce chiffre se remplira
              quand le canal ouvrira ce chemin, sans rien changer d’autre.
            </p>
          </section>
        </>
      )}
    </main>
  );
}

function Chiffre({
  titre,
  valeur,
  attenue = false,
}: {
  readonly titre: string;
  readonly valeur: string;
  /**
   * **Un chiffre indisponible se distingue d'un chiffre nul À L'ŒIL, pas seulement dans le
   * texte à côté.** Sans cela, « indisponible » en gras de la même taille qu'un nombre se
   * lit comme une valeur — et c'est la confusion que tout ce mécanisme existe pour éviter.
   */
  readonly attenue?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-muted-foreground text-sm font-normal">{titre}</CardTitle>
      </CardHeader>
      <CardContent>
        <span
          className={
            attenue
              ? 'text-muted-foreground text-base font-normal italic'
              : 'text-2xl font-semibold'
          }
        >
          {valeur}
        </span>
      </CardContent>
    </Card>
  );
}
